import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FirebaseService } from '@vsolv/packages/firebase/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Customer } from '@wsphere/customers/domain';
import { Distributor } from '@wsphere/distributors/domain';
import { Provisioning, Warranty } from '@wsphere/warranties/domain';
import { BehaviorSubject, Subject, catchError, firstValueFrom, of } from 'rxjs';
import { EvaluatedAddon } from '../../policy';

@Injectable()
export class ProvisioningSessionService {
  constructor(
    private http: HttpClient,
    private firebaseSvc: FirebaseService,
    private toastSvc: ToastService
  ) {}

  private session$ = new BehaviorSubject<Provisioning.ProvisioningSession | null>(null);
  distributor$ = new BehaviorSubject<Distributor.Model | null>(null);
  preview$ = new BehaviorSubject<Provisioning.WarrantyPreview | null>(null);
  addons$ = new BehaviorSubject<{ planId: string; termId: string; addons: EvaluatedAddon[] }[] | null>(null);
  data$ = new BehaviorSubject<InitialData>({});
  sessionComplete$ = new BehaviorSubject<boolean>(false);
  close$ = new Subject<void>();

  /**
   * Admin-dashboard-only "quote before buying" markup, keyed per (term, plan) so every card on the
   * Plan step can carry its own preview value before any plan is actually selected. Cents.
   */
  planMarkups$ = new BehaviorSubject<Record<string, number>>({});
  /** "Hide Markup Entry" toggle on the Plan step — hides the input boxes, not the marked-up price. */
  hideMarkupEntry$ = new BehaviorSubject<boolean>(false);

  static planMarkupKey(termId: string, planId: string): string {
    return `${termId}|${planId}`;
  }

  getPlanMarkup(termId: string, planId: string): number {
    return this.planMarkups$.value[ProvisioningSessionService.planMarkupKey(termId, planId)] ?? 0;
  }

  setPlanMarkup(termId: string, planId: string, cents: number) {
    const key = ProvisioningSessionService.planMarkupKey(termId, planId);
    this.planMarkups$.next({ ...this.planMarkups$.value, [key]: Math.max(0, cents) });
  }

  resetPlanMarkups() {
    this.planMarkups$.next({});
  }

  async get(sessionId: string) {
    return await firstValueFrom(
      this.http.get<Provisioning.ProvisioningSession>(`/api/provision-session/${sessionId}`).pipe(
        catchError(_err => {
          return of(null);
        })
      )
    );
  }
  /**
   * Cache for evaluated-session responses, keyed by `${sessionId}|${sessionHash}|${ignoreVisibility}|${termId ?? ''}`.
   *
   * PLAN_FIRST checkout fans out N parallel evaluate calls (one per term the selected plan offers).
   * Without memoization, every change-detection cycle on the picker (or every plan-strip click that
   * doesn't change the term set) would re-hit the network. We cache the in-flight Promise so:
   *   - Identical concurrent calls dedupe to a single HTTP request.
   *   - Subsequent calls return the resolved value instantly.
   *
   * Invalidated by `invalidateEvaluateCache()` on session updates so stale property values never
   * leak into a fresh evaluation.
   */
  private evaluateCache = new Map<string, Promise<Provisioning.EvaluatedSessionPlans>>();

  async evaluateSession(
    sessionId: string,
    sessionHash: string | null | undefined,
    ignoreVisibility: boolean,
    termId?: string
  ) {
    const cacheKey = `${sessionId}|${sessionHash ?? ''}|${ignoreVisibility}|${termId ?? ''}`;
    const cached = this.evaluateCache.get(cacheKey);
    if (cached) return cached;

    const params: Record<string, string | boolean> = { ignoreVisibility };
    if (termId) params['termId'] = termId;

    const promise = firstValueFrom(
      this.http.get<Provisioning.EvaluatedSessionPlans>(`/api/provision-session/${sessionId}/evaluate`, { params })
    ).catch(err => {
      // Drop the cache entry on failure so a retry actually hits the network.
      this.evaluateCache.delete(cacheKey);
      throw err;
    });

    this.evaluateCache.set(cacheKey, promise);
    return promise;
  }

  /**
   * Clear cached evaluate responses for a session — call this whenever session state changes in a
   * way that could affect evaluation (term swap, asset edit, property update, etc.). Without this
   * a stale plan/coverage payload would be served from cache after the session mutates.
   */
  invalidateEvaluateCache(sessionId?: string) {
    if (!sessionId) {
      this.evaluateCache.clear();
      return;
    }
    for (const key of this.evaluateCache.keys()) {
      if (key.startsWith(`${sessionId}|`)) this.evaluateCache.delete(key);
    }
  }

  async create(dto: Provisioning.CreateProvisioningSessionDto) {
    const response = await firstValueFrom(
      this.http.post<Provisioning.CreateProvisioningSessionResponse>(`/api/provision-session`, dto)
    );

    if (response.signInToken) {
      await this.firebaseSvc.customTokenSignIn(response.signInToken.signInToken, response.signInToken.tenantId);
    }

    return response.session;
  }

  async update(sessionId: string, dto: Provisioning.UpdateProvisioningSessionDto) {
    return await firstValueFrom(
      this.http.put<Provisioning.ProvisioningSession>(`/api/provision-session/${sessionId}`, dto)
    );
  }

  async preview(sessionId: string, hash: string) {
    return await firstValueFrom(
      this.http.post<Provisioning.WarrantyPreview>(`/api/provision-session/${sessionId}/preview`, { hash })
    );
  }

  async confirm(
    sessionId: string,
    hash: string,
    paymentMethodId?: string,
    multiPhasePayment?: Provisioning.MultiPhasePayment
  ) {
    return await firstValueFrom(
      this.http.post<Warranty.Model>(`/api/provision-session/${sessionId}/confirm`, {
        hash,
        paymentMethodId,
        multiPhasePayment,
      })
    );
  }

  getCurrentSession() {
    return this.session$.asObservable();
  }

  /**
   * Fields in the update DTO that affect the evaluate endpoint result.
   * Evaluation uses: asset property values, customer, distributor, shipping address,
   * policy (plan set), and the session term (TERM_FIRST mode).
   * Plan selection, addon toggles, markup, and payment schedule do NOT change which
   * plans are available or their prices, so they must not bust the evaluate cache.
   */
  private static readonly EVALUATE_AFFECTING_FIELDS: ReadonlyArray<keyof Provisioning.UpdateProvisioningSessionDto> = [
    'distributorId',
    'policyId',
    'assetInfo',
    'assetDraft',
    'customerInfo',
    'shippingAddress',
    'policyTermId',
  ];

  /**
   * True when distributor, customer, policy, or asset actually changed between the previous and
   * new persisted session (VS-1116 "pre plan selection" reset). Several wizard steps resend their
   * field on every "Next" click regardless of whether the user edited anything — e.g. the Customer
   * step always includes `distributorId`/`customerInfo`, and Policy/Asset always include
   * `policyId`/`assetInfo` in both their "same value" and "changed value" branches (see
   * `provision-customer-info`, `provision-policy`, `provision-asset` `nextPage()`). Checking DTO
   * key presence would therefore wipe per-plan markup quotes on ordinary back/forward navigation
   * that changed nothing — comparing the actual persisted values avoids that.
   */
  private static hasMarkupResetChange(
    previous: Provisioning.ProvisioningSession | null,
    next: Provisioning.ProvisioningSession
  ): boolean {
    if (!previous) return false;
    return (
      previous.distributorId !== next.distributorId ||
      previous.customerId !== next.customerId ||
      previous.policyId !== next.policyId ||
      previous.assetId !== next.assetId
    );
  }

  async updateSession(preview: { sessionId: string; data: Provisioning.UpdateProvisioningSessionDto }) {
    try {
      const previousSession = this.session$.value;
      const session = await this.update(preview.sessionId, preview.data);
      // Invalidate only when evaluation-affecting fields changed, and do so before `next(session)`
      // so synchronous subscribers cannot observe stale cached evaluations.
      const affectsEvaluation = ProvisioningSessionService.EVALUATE_AFFECTING_FIELDS.some(
        field => field in preview.data
      );
      if (affectsEvaluation) {
        this.invalidateEvaluateCache(preview.sessionId);
      }
      if (ProvisioningSessionService.hasMarkupResetChange(previousSession, session)) {
        this.resetPlanMarkups();
      }
      this.session$.next(session);
    } catch (err: unknown) {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong!',
      });
      console.error(err);
      throw new Error();
    }
  }

  async refreshSession(session?: Provisioning.ProvisioningSession) {
    if (this.session$.value) {
      if (session) {
        if (this.session$.value.hash !== session.hash) this.session$.next(session);
      } else {
        const id = this.session$.value.id;
        const session = await this.get(id);
        this.session$.next(session);
      }
    } else {
      if (session) {
        this.session$.next(session);
      }
    }
  }

  clearSession() {
    this.session$.next(null);
    this.invalidateEvaluateCache();
    this.resetPlanMarkups();
    this.hideMarkupEntry$.next(false);
  }
}

export interface InitialData {
  customer?: Customer.Model;
  customerInfo?: {
    name: string | undefined;
    email: string | null;
    phone: string | null | undefined;
  };
  distributor?: {
    referenceId: string;
    parentId: string | undefined;
  };
  distributorId?: string | null;
  viewablePlansAndTerms?: Provisioning.ViewablePlansAndTerms;
  expiryDate?: Date;
}
